/**
 * Runtime resolution of localized App-DATA labels (multi-language EigenApps).
 *
 * This mirrors `@data-root/types` `localized.ts` (the framework contract) so the runtime can resolve
 * `{ $i18n: { en, es, ... } }` labels BEFORE the published `@data-root/types` republishes with the
 * helper. Once the framework package ships `resolveLocalized`/`localizeApp`, this file can re-export
 * from it and be deleted.
 *
 * The whole App definition is run through `localizeApp(app, locale)` ONCE at load (the route loader),
 * so every downstream view/renderer keeps reading plain `string` labels - no per-site changes, and a
 * new language needs no code change. A plain-string App passes through unchanged (zero regression).
 */

const DEFAULT_LOCALE = "en";

interface LocalizedString {
  $i18n: Record<string, string>;
}

function isLocalizedString(v: unknown): v is LocalizedString {
  if (typeof v !== "object" || v === null) return false;
  const i18n = (v as { $i18n?: unknown }).$i18n;
  return typeof i18n === "object" && i18n !== null && !Array.isArray(i18n);
}

/** Resolve a plain string or `{ $i18n }` label to the active-locale string. Total; never throws. */
export function resolveLocalized(
  value: unknown,
  locale?: string | null,
  fallbackLocale: string = DEFAULT_LOCALE,
): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (!isLocalizedString(value)) return "";
  const map = value.$i18n || {};
  const keys = Object.keys(map);
  if (keys.length === 0) return "";
  const want = (locale || fallbackLocale || DEFAULT_LOCALE).toLowerCase();
  const wantBase = want.split(/[-_]/)[0];
  const fb = (fallbackLocale || DEFAULT_LOCALE).toLowerCase();
  const fbBase = fb.split(/[-_]/)[0];
  const findKey = (code: string) => keys.find((k) => k.toLowerCase() === code);
  const hit = findKey(want) ?? findKey(wantBase) ?? findKey(fb) ?? findKey(fbBase) ?? keys[0];
  return hit ? map[hit] : "";
}

/**
 * Deep-resolve every `{ $i18n }` label anywhere in the App definition to its `locale` string,
 * returning a structurally-identical copy with plain-string labels. Apply to the App DEFINITION
 * (schema/views/actions/menus), NOT to user row DATA.
 */
export function localizeApp<T>(node: T, locale?: string | null, fallbackLocale: string = DEFAULT_LOCALE): T {
  return deepLocalize(node, locale, fallbackLocale) as T;
}

function deepLocalize(node: unknown, locale: string | null | undefined, fallbackLocale: string): unknown {
  if (node == null) return node;
  if (isLocalizedString(node)) return resolveLocalized(node, locale, fallbackLocale);
  if (Array.isArray(node)) return node.map((x) => deepLocalize(x, locale, fallbackLocale));
  if (typeof node === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(node as Record<string, unknown>)) {
      out[k] = deepLocalize((node as Record<string, unknown>)[k], locale, fallbackLocale);
    }
    return out;
  }
  return node;
}
